import { resolveMount } from './resolve-mount';
import { logAnalyticsEvent } from './firebase-analytics';
import { ga4EventFor } from './ga4-events';

// The Web SDK ID exactly as AppsFlyer's dashboard shows it for the Symphony web app
// (My Apps → the Web app). It is used VERBATIM by the SDK — `getWebAppId()` returns
// this string with no transformation, and it becomes both the `site-id` query param
// and `data.webAppId` on every event POST. There is no `websdk:` prefix to add: the
// SDK bundle contains no such string, and prefixing it addressed every event to a web
// app that does not exist, so AppsFlyer ACKed the POST with a 200 and then discarded
// it — which is how all of these events silently reported nothing at all.
export const APPSFLYER_WEB_DEV_KEY = 'website-symphony.wix.com';

type AfEventValue = Record<string, string | number | boolean>;

type AfFn = ((...args: unknown[]) => void) & {
  q?: unknown[];
  id?: unknown;
  plugins?: Record<string, unknown>;
};

declare global {
  interface Window {
    AppsFlyerSdkObject?: string;
    AF?: AfFn;
  }
}

let initialized = false;

// Symphony product only — the symphony.wix.com root and the /symphony-web mount;
// /orion-web is excluded. Read off the live URL rather than the brand context so the
// answer is correct on the very first call: the context seeds from SSR ('Orion') and
// only flips to the real value in a post-mount effect, which is too late for an event
// fired during mount.
const appsFlyerEnabled = (): boolean =>
  typeof window !== 'undefined' && resolveMount(window.location.pathname) !== '/orion-web';

export const initAppsFlyer = (): void => {
  if (initialized) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!APPSFLYER_WEB_DEV_KEY) return;
  if (!appsFlyerEnabled()) return;
  initialized = true;

  window.AppsFlyerSdkObject = 'AF';
  const af: AfFn =
    window.AF ||
    function (this: unknown, ...args: unknown[]) {
      (af.q = af.q || []).push([Date.now(), ...args]);
    };
  af.id = af.id || { pba: { webAppId: APPSFLYER_WEB_DEV_KEY } };
  af.plugins = {};
  window.AF = af;

  const script = document.createElement('script');
  script.async = true;
  // `st=pba` enables the People-Based Attribution (web in-app event) plugin.
  script.src = 'https://websdk.appsflyer.com?st=pba';
  const first = document.getElementsByTagName('script')[0];
  first?.parentNode?.insertBefore(script, first);
};

// Init lazily on the first event, so no caller can lose an event to effect ordering.
// `_app` boots the SDK eagerly, but React flushes a CHILD's mount effect before its
// parent's — so a page firing an event from its own mount effect used to run before
// `_app`'s init, find no `window.AF`, and drop the event on every hard load (only
// client-side navigations, where `_app` was already mounted, reported). Init is
// idempotent and self-gating, so calling it here is safe and keeps Orion a no-op.
const logEvent = (eventName: string, eventValue: AfEventValue = {}): void => {
  if (typeof window === 'undefined') return;
  // Mirror every AppsFlyer event to Google Analytics (GA4) — TRANSLATED, not verbatim. GA4 reads
  // revenue from `value`/`currency` and recognises only its own commerce names, so the af_* shape
  // that is correct for AppsFlyer lands in GA4 as a custom event carrying no revenue. See
  // `ga4-events.ts` for the mapping and why it replaces rather than duplicates. Self-gating and
  // idempotent, so it's a no-op where Analytics isn't supported.
  // `null` for a purchase: GA4 hears about purchases only as the server's `symphony_premium`.
  const ga4 = ga4EventFor(eventName, eventValue);
  if (ga4) logAnalyticsEvent(ga4.name, ga4.params);
  initAppsFlyer();
  if (!window.AF) return;
  window.AF('pba', 'event', { eventType: 'EVENT', eventName, eventValue });
};

// The last id pushed to the SDK, so we only call out when it actually changes.
let lastCustomerUserId: string | null = null;

// Associate AppsFlyer's People-Based Attribution with the stable Wix user id, so events
// dedupe per PERSON instead of per browser (a user on phone + laptop, a reinstall, or a
// cleared browser would otherwise each count as a new "user"). Idempotent — only pushes
// when the id changes. Sent as a string with no PII (a Wix user id), per AppsFlyer's CUID
// guidance. Mirrors native `SymphonyAppsFlyer.applyCustomerUserId`.
export const setAppsFlyerCustomerUserId = (userId: string | null | undefined): void => {
  if (typeof window === 'undefined') return;
  const id = (userId ?? '').trim();
  if (!id || id === lastCustomerUserId) return;
  lastCustomerUserId = id;
  initAppsFlyer();
  if (!window.AF) return;
  window.AF('pba', 'setCustomerUserId', id);
};

export const logOnboardingStart = (): void => logEvent('symphony_onboarding_start');

export const logSignup = (): void =>
  logEvent('af_complete_registration', { af_registration_method: 'wix' });

export const logLogin = (): void => logEvent('af_login');

export const logActiveUser = (): void => logEvent('symphony_active_user');

// "Finish Onboarding" — the AppsFlyer PREDEFINED tutorial-completion event, no params.
// Mirrors native's `SymphonyAppsFlyer.logOnboardingComplete` (iOS AppsFlyer.swift /
// Android SymphonyAppsFlyer.kt) 1:1 — same name, same absence of params.
export const logOnboardingComplete = (): void => logEvent('af_tutorial_completion');

// `origin` is what sent the visitor here (see lib/upgrade-origin). Omitted rather than defaulted when
// the open was untagged — a made-up default files every unattributed view under one real surface.
export const logPackagePickerView = (origin?: string): void =>
  logEvent('af_content_view', { af_content_type: 'credits_packages', ...(origin ? { origin } : {}) });

export const logPurchasePageView = (): void => logEvent('af_initiated_checkout');

// PBA (the web SDK plugin) reads revenue off `eventRevenue`/`eventRevenueCurrency` — AppsFlyer's own
// docs for the PBA pipeline are explicit that these must be populated for the event to land in PBA
// dashboards, even when `af_revenue`/`af_currency` (the mobile-SDK convention) are also present. We
// send both: the af_* pair mirrors native and is what a mobile-SDK-trained reader expects, but PBA's
// own field names are what its web pipeline actually keys off.
// `origin` is the picker's validated entry point (lib/upgrade-origin.ts), stashed at checkout start and
// read back on the return (docs/compliance/bi.md §2) — the same value the picker-view event carries.
export const logPurchaseSuccess = (revenue: number, currency: string, transactionId?: string, origin?: string): void =>
  logEvent('af_purchase', {
    af_revenue: revenue,
    af_currency: currency,
    eventRevenue: revenue,
    eventRevenueCurrency: currency,
    // Ignored by AppsFlyer, read by the GA4 mapper: GA4 de-duplicates purchases on
    // `transaction_id`, which is what makes a reloaded return page harmless there too.
    ...(transactionId ? { transaction_id: transactionId } : {}),
    ...(origin ? { origin } : {}),
  });

export const logDownloadApp = (platform: 'ios' | 'android'): void =>
  logEvent('symphony_download_app', { platform });

const LOGIN_PENDING_KEY = 'appsflyer.loginPending';

export const markLoginPending = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(LOGIN_PENDING_KEY, '1');
  } catch {
    /* sessionStorage unavailable (private mode / blocked) — login just won't be tracked */
  }
};

export const logLoginIfPending = (): void => {
  if (typeof window === 'undefined') return;
  try {
    if (window.sessionStorage.getItem(LOGIN_PENDING_KEY) !== '1') return;
    window.sessionStorage.removeItem(LOGIN_PENDING_KEY);
  } catch {
    return;
  }
  logLogin();
};

// Persisted per-browser latches for the three onboarding-arc product events, so each
// reports at most once per browser. Named for the PRODUCT — a new key does not take the
// `orion-web:` localStorage prefix (see orion-web/CLAUDE.md: "A NEW key must not inherit
// the prefix"). The per-browser cost is the same one gtm-funnel documents: a second
// device/browser can emit a duplicate; deduping for real needs a server-side flag.
const AF_ONBOARDING_START_KEY = 'symphony-af-onboarding-start';
const AF_ONBOARDING_FINISH_KEY = 'symphony-af-onboarding-finish';
const AF_ACTIVE_USER_KEY = 'symphony-af-active-user';

// Fire `emit` unless this browser already latched `key`. SSR-safe, and on unavailable
// storage (private mode / blocked) it SKIPS rather than repeats — a browser that can't
// hold the latch stays silent instead of re-reporting on every send.
const fireOnce = (key: string, emit: () => void): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    if (window.localStorage.getItem(key) === '1') return false;
    window.localStorage.setItem(key, '1');
  } catch {
    return false;
  }
  emit();
  return true;
};

// Per-SESSION latch for the purchase event (keyed on the Stripe Checkout session id, not
// the browser). The verification that confirms a checkout is genuinely paid can be retried
// or resumed on a later page load (see packages.tsx) — this makes that safe by construction:
// however many times the same session gets re-verified, `af_purchase` reports at most once.
const AF_PURCHASE_KEY = 'symphony-af-purchase';

// Returns whether this call actually reported the event (false = this session was already
// latched by an earlier attempt) — purely a diagnostic signal for the caller's own logging.
export const logPurchaseSuccessOnce = (sessionId: string, revenue: number, currency: string, origin?: string): boolean =>
  fireOnce(`${AF_PURCHASE_KEY}:${sessionId}`, () => logPurchaseSuccess(revenue, currency, sessionId, origin));

// Signup — `af_complete_registration`. Native fires it at account creation (finalizeLogin
// `isSignup`). Web can't see "account created" at auth time — auth is an automatic bounce to
// Wix Identity, which returns no new-account signal — so we detect a BRAND-NEW account from its
// creation timestamp (`getMyAccount().dateCreated`, surfaced on `/api/me`): an account created
// within `SIGNUP_FRESH_MS` before this first authenticated load is a signup. Latched per ACCOUNT
// so it fires at most once ever; the freshness gate stops an existing user's first web visit (old
// account) — including one who signed up on native — from being counted again here.
const AF_SIGNUP_KEY = 'symphony-af-signup';
const SIGNUP_FRESH_MS = 60 * 60 * 1000; // signup→first app load is seconds; 1h is generous for skew.

// The primary, correct trigger: fire once for a genuinely new account.
export const logSignupIfNewAccount = (
  userId: string | null | undefined,
  accountCreatedAt: string | null | undefined,
): void => {
  const id = (userId ?? '').trim();
  if (!id || !accountCreatedAt) return;
  const created = Date.parse(accountCreatedAt);
  if (Number.isNaN(created) || Date.now() - created > SIGNUP_FRESH_MS) return;
  fireOnce(`${AF_SIGNUP_KEY}:${id}`, logSignup);
};

// Fallback used only when the account age is unavailable (e.g. `getMyAccount` denied): fire signup
// once per account at phone verification, the pre-existing web trigger. Shares the per-account latch
// with `logSignupIfNewAccount`, so the two paths can never double-fire.
export const logSignupOnce = (userId: string | null | undefined): void => {
  const id = (userId ?? '').trim();
  if (!id) return;
  fireOnce(`${AF_SIGNUP_KEY}:${id}`, logSignup);
};

// A message the user sent. Which product event it is depends on whether onboarding has
// finished — the caller passes the web funnel's own onboarding-finished signal
// (`hasFired('finish')` in gtm-funnel, the workspace-milestone latch). Before it, the
// first send STARTS onboarding; after it, the first send is the ACTIVE-USER activation.
// Each side is latched, so only the first send on each side of the milestone reports.
export const reportAppsFlyerSend = (onboardingFinished: boolean): void => {
  if (onboardingFinished) {
    fireOnce(AF_ACTIVE_USER_KEY, logActiveUser);
  } else {
    fireOnce(AF_ONBOARDING_START_KEY, logOnboardingStart);
  }
};

// The onboarding-finish product event — fired once per browser when a LIVE workspace
// milestone lands. Its own latch, independent of the send events above.
export const reportAppsFlyerOnboardingComplete = (): void => {
  fireOnce(AF_ONBOARDING_FINISH_KEY, logOnboardingComplete);
};

// Test-only: clear the three product-event latches so a suite can exercise the
// fire-once branch from a clean slate. Kept here so the key shapes stay private.
export const resetAppsFlyerProductEventLatches = (): void => {
  if (typeof window === 'undefined') return;
  try {
    [AF_ONBOARDING_START_KEY, AF_ONBOARDING_FINISH_KEY, AF_ACTIVE_USER_KEY].forEach((key) =>
      window.localStorage.removeItem(key),
    );
  } catch {
    /* nothing to reset */
  }
};
